import { z } from 'zod';
import { PolicyBatchStatus } from '@/shared/enums/policy-batch-status.enum';
import type {
  PolicyBatchItem,
  PolicyBatchPatch,
} from '@/services/integrations';

/**
 * Datas do balde são tratadas como HORÁRIO DE PAREDE, sem conversão de fuso.
 *
 * `batchDate` é gravado pela importação como `yyyy-MM-ddTHH:mm:ss.SSS` (SEM timezone),
 * então qualquer `new Date()` no meio do caminho deslocaria o valor. Recortar a string
 * mostra exatamente o que está no banco e devolve exatamente o que o usuário digitou.
 */
export function isoToDateTimeLocal(iso?: string | null): string {
  if (!iso) return '';
  return iso.slice(0, 16);
}

export function dateTimeLocalToIso(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return `${trimmed}:00.000`;
}

/** Rascunho do formulário: tudo string, como sai dos inputs. */
export const policyBatchEditSchema = z.object({
  channelCode: z
    .string()
    .trim()
    .min(1, 'Origem de emissão é obrigatória')
    .refine(value => Number(value) > 0, 'Origem de emissão inválida'),
  batchId: z
    .string()
    .trim()
    .min(1, 'Nº do balde é obrigatório')
    .refine(value => Number(value) > 0, 'Nº do balde inválido'),
  requestId: z
    .string()
    .trim()
    .min(1, 'ID da solicitação é obrigatório')
    .refine(value => Number(value) > 0, 'ID da solicitação inválido'),
  batchDate: z.string().trim().min(1, 'Data do balde é obrigatória'),
  status: z.enum([
    PolicyBatchStatus.Available,
    PolicyBatchStatus.Reserved,
    PolicyBatchStatus.Used,
  ]),
  reservedDate: z.string(),
  usedDate: z.string(),
  userName: z.string(),
});

export type PolicyBatchEditDraft = z.infer<typeof policyBatchEditSchema>;

export function buildDraftFromItem(
  item: PolicyBatchItem
): PolicyBatchEditDraft {
  return {
    channelCode: String(item.channelCode ?? ''),
    batchId: String(item.batchId ?? ''),
    requestId: String(item.requestId ?? ''),
    batchDate: isoToDateTimeLocal(item.batchDate),
    status: item.status,
    reservedDate: isoToDateTimeLocal(item.reservedDate),
    usedDate: isoToDateTimeLocal(item.usedDate),
    userName: item.userName ?? '',
  };
}

/**
 * Monta o patch APENAS com o que mudou.
 *
 * Enviar o formulário inteiro reescreveria `batchDate`/`reservedDate` com a versão
 * recortada pelo input mesmo quando o usuário nem tocou no campo — o diff evita essa
 * perda silenciosa de precisão.
 */
export function buildPatchFromDraft(
  item: PolicyBatchItem,
  draft: PolicyBatchEditDraft
): PolicyBatchPatch {
  const original = buildDraftFromItem(item);
  const patch: PolicyBatchPatch = {};

  if (draft.channelCode !== original.channelCode)
    patch.channelCode = Number(draft.channelCode);
  if (draft.batchId !== original.batchId) patch.batchId = Number(draft.batchId);
  if (draft.requestId !== original.requestId)
    patch.requestId = Number(draft.requestId);
  if (draft.batchDate !== original.batchDate)
    patch.batchDate = dateTimeLocalToIso(draft.batchDate) ?? undefined;
  if (draft.status !== original.status) patch.status = draft.status;
  // `null` limpa o campo no DynamoDB; `undefined` o deixaria intacto.
  if (draft.reservedDate !== original.reservedDate)
    patch.reservedDate = dateTimeLocalToIso(draft.reservedDate);
  if (draft.usedDate !== original.usedDate)
    patch.usedDate = dateTimeLocalToIso(draft.usedDate);
  if (draft.userName !== original.userName)
    patch.userName = draft.userName.trim() || null;

  return patch;
}

export function isEmptyPatch(patch: PolicyBatchPatch): boolean {
  return Object.keys(patch).length === 0;
}
